import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Grid,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  FiberManualRecord as DotIcon,
  AutoAwesome as GenerateIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import DesignPreviewBanner from '../components/DesignPreviewBanner';
import { SESSION_PREVIEWS } from '../data/sessionTypes';

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Renders one generated deliverable by its `kind`.
function DeliverableCard({ deliverable }) {
  const { title, icon: Icon, kind, body } = deliverable;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(13,148,136,0.12)',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
        </Stack>

        {kind === 'paragraph' && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            {body}
          </Typography>
        )}

        {(kind === 'bullets' || kind === 'checks') && (
          <List dense disablePadding>
            {body.map((item) => (
              <ListItem key={item} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 30, mt: '4px' }}>
                  {kind === 'checks' ? (
                    <UncheckedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <DotIcon sx={{ fontSize: 9, color: 'primary.main' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

export default function SessionPreview() {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = SESSION_PREVIEWS[type];

  // phase: 'idle' | 'recording' | 'generated'
  const [phase, setPhase] = useState('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [stopDialog, setStopDialog] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // Unknown type → back to the hub.
  useEffect(() => {
    if (!config) navigate('/sessions', { replace: true });
  }, [config, navigate]);
  if (!config) return null;

  const SessionIcon = config.icon;

  // Simulated recording — no microphone is accessed; this is a design preview.
  const startRecording = () => {
    setRecordingTime(0);
    setPhase('recording');
    timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
  };
  const stopRecording = () => {
    clearInterval(timerRef.current);
    setStopDialog(true);
  };
  const generate = () => {
    setStopDialog(false);
    setPhase('generated');
  };
  const reset = () => {
    setPhase('idle');
    setRecordingTime(0);
  };

  return (
    <Box>
      <DesignPreviewBanner />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/sessions')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SessionIcon />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={600} sx={{ lineHeight: 1.1 }}>
              {config.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.tagline}
            </Typography>
          </Box>
        </Box>
        <Chip label="Preview" color="warning" variant="outlined" sx={{ fontWeight: 700 }} />
      </Box>

      {/* Context card (analogous to the patient card on a clinical encounter) */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              {config.contextLabel}
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              {config.context.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.context.subtitle}
            </Typography>
          </Box>
          <Chip
            label={phase === 'recording' ? 'Recording' : phase === 'generated' ? 'Generated' : 'Ready'}
            color={phase === 'recording' ? 'error' : phase === 'generated' ? 'success' : 'default'}
            icon={phase === 'recording' ? <MicIcon /> : <CheckIcon />}
          />
        </CardContent>
      </Card>

      {/* Capture card */}
      <Card sx={{ mb: 3, minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent sx={{ textAlign: 'center', width: '100%' }}>
          {phase === 'recording' ? (
            <Box>
              <Typography variant="h2" fontWeight={700} color="error.main">
                {formatTime(recordingTime)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Simulated recording (no microphone is used in preview)
              </Typography>
              <IconButton
                onClick={stopRecording}
                sx={{ width: 80, height: 80, bgcolor: 'error.main', color: '#fff', '&:hover': { bgcolor: 'error.dark' } }}
              >
                <StopIcon fontSize="large" />
              </IconButton>
            </Box>
          ) : phase === 'generated' ? (
            <Box>
              <CheckIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Deliverables generated
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Review and edit below, then deliver. (Preview — nothing is saved.)
              </Typography>
              <Button variant="outlined" startIcon={<ReplayIcon />} onClick={reset}>
                Start over
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Ready to capture this {config.name.toLowerCase()}
              </Typography>
              <Button variant="contained" size="large" startIcon={<MicIcon />} onClick={startRecording} sx={{ py: 2, px: 4 }}>
                Start Recording
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Generated deliverables preview */}
      {phase === 'generated' ? (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <GenerateIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={700}>
              Generated deliverables
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            {config.deliverables.map((d) => (
              <Grid item xs={12} md={6} key={d.title}>
                <DeliverableCard deliverable={d} />
              </Grid>
            ))}
          </Grid>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
            <Button variant="contained" disabled>
              Approve &amp; deliver
            </Button>
            <Button variant="outlined" disabled>
              Edit deliverables
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Actions are disabled in this design preview.
            </Typography>
          </Stack>
        </Box>
      ) : (
        // Show what *will* be produced + tips before recording.
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Deliverables this session will produce
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {config.deliverables.map((d) => {
                    const Icon = d.icon;
                    return (
                      <Stack direction="row" spacing={1.2} alignItems="center" key={d.title}>
                        <Icon fontSize="small" sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" fontWeight={600}>
                          {d.title}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Session tips
                </Typography>
                <List dense disablePadding sx={{ mt: 0.5 }}>
                  {config.tips.map((tip) => (
                    <ListItem key={tip} disableGutters sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 26, mt: '5px' }}>
                        <DotIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText primary={tip} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Divider sx={{ mt: 4, mb: 2, opacity: 0 }} />

      {/* Stop → generate dialog */}
      <Dialog open={stopDialog} onClose={() => setStopDialog(false)}>
        <DialogTitle>Generate deliverables?</DialogTitle>
        <DialogContent>
          <Typography>Recording time: {formatTime(recordingTime)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ScribeLive will generate {config.deliverables.map((d) => d.title).join(', ')} from this {config.name.toLowerCase()}. You can review and edit before delivering.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStopDialog(false); startRecording(); }}>Continue recording</Button>
          <Button variant="contained" onClick={generate} startIcon={<GenerateIcon />}>
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
